import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import styles from './Page.module.css';
import ModalExportarLista from '../components/common/ModalExportarLista';

const fmtBRL = v => parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const COMPARADORES = [
  { v: 'gte', l: '≥ maior ou igual a' },
  { v: 'gt',  l: '> maior que' },
  { v: 'eq',  l: '= igual a' },
  { v: 'lte', l: '≤ menor ou igual a' },
  { v: 'lt',  l: '< menor que' },
];

const PRESETS = [
  { id: 'todos',    label: 'Todos',           comparador: 'gte', valor: 0 },
  { id: 'com_saldo', label: 'Com saldo (>0)', comparador: 'gt',  valor: 0 },
  { id: 'sem_saldo', label: 'Sem saldo (=0)', comparador: 'eq',  valor: 0 },
];

const COLUNAS_EXPORT = [
  { key: 'codigo',   label: 'Código' },
  { key: 'nome',     label: 'Nome' },
  { key: 'cpf_cnpj', label: 'CPF/CNPJ' },
  { key: 'fone1',    label: 'Telefone' },
  { key: 'saldo_pontos', label: 'Saldo (pts)' },
  { key: 'credito_disponivel', label: 'Crédito disponível R$', render: l => fmtBRL(l.credito_disponivel) },
];

// Resumo geral (1 linha por cliente) do saldo de pontos de fidelidade — não
// confundir com Extrato de Pontos (1 linha por lançamento, de UM cliente).
export default function ExtratoGeralFidelidade() {
  const [comparador, setComparador] = useState('gte');
  const [valor, setValor] = useState(0);
  const [modalExportar, setModalExportar] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['fidelidade-extrato-geral', comparador, valor],
    queryFn: () => api.get('/clientes/fidelidade/extrato-geral', { params: { comparador, valor } }).then(r => r.data),
  });

  const linhas = data?.linhas || [];
  const resumo = data?.resumo || { total_clientes: 0, total_pontos: 0, total_credito_disponivel: 0 };
  const presetAtivo = PRESETS.find(p => p.comparador === comparador && p.valor === valor)?.id;

  return (
    <div>
      <div className={styles.topbar}>
        <h2 className={styles.titulo}>Resumo de Pontos de Fidelidade</h2>
        <div className={styles.acoes}>
          <button className={styles.btnSecundario} onClick={() => setModalExportar(true)} disabled={!linhas.length}>
            📤 Exportar / Imprimir
          </button>
        </div>
      </div>

      <div className={styles.filtros}>
        {PRESETS.map(p => (
          <button key={p.id}
            className={`${styles.filtroBtn} ${presetAtivo === p.id ? styles.filtroBtnAtivo : ''}`}
            onClick={() => { setComparador(p.comparador); setValor(p.valor); }}>
            {p.label}
          </button>
        ))}
      </div>

      <div className={styles.acoes} style={{ marginBottom: 14 }}>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Saldo de pontos</span>
        <select className={styles.select} value={comparador} onChange={e => setComparador(e.target.value)}>
          {COMPARADORES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
        <input type="number" className={styles.input} style={{ width: 90 }}
          value={valor} onChange={e => setValor(parseInt(e.target.value) || 0)} />
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div className={styles.tabela} style={{ padding: '10px 18px', flex: '1 1 160px' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Clientes listados</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{resumo.total_clientes}</div>
        </div>
        <div className={styles.tabela} style={{ padding: '10px 18px', flex: '1 1 160px' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Total de pontos</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{resumo.total_pontos}</div>
        </div>
        <div className={styles.tabela} style={{ padding: '10px 18px', flex: '1 1 160px' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Crédito disponível total</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--success)' }}>R$ {fmtBRL(resumo.total_credito_disponivel)}</div>
        </div>
      </div>

      {isLoading ? <p>Carregando...</p> : !linhas.length ? (
        <div className={styles.tabela} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
          Nenhum cliente encontrado com esse filtro.
        </div>
      ) : (
        <div className={styles.tabela}>
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>CPF/CNPJ</th>
                <th>Telefone</th>
                <th style={{ textAlign: 'right' }}>Saldo (pts)</th>
                <th style={{ textAlign: 'right' }}>Crédito disponível</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map(l => (
                <tr key={l.id}>
                  <td><strong>{l.nome}</strong></td>
                  <td>{l.cpf_cnpj || '—'}</td>
                  <td>{l.fone1 || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{l.saldo_pontos}</td>
                  <td style={{ textAlign: 'right', color: 'var(--success)' }}>R$ {fmtBRL(l.credito_disponivel)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalExportar && (
        <ModalExportarLista
          titulo="Resumo de Pontos de Fidelidade"
          colunas={COLUNAS_EXPORT}
          dados={linhas}
          nomeArquivo={`fidelidade_resumo_${new Date().toISOString().slice(0, 10)}`}
          onClose={() => setModalExportar(false)}
        />
      )}
    </div>
  );
}
